import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import {
  ArrowCheckIcon,
  CancelIcon,
  EditPenIcon,
  EmailIcon,
  NoteIcon,
  ResetIcon,
  ScannerIcon,
  ShowIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { FormBuilder, FormBuilderSkeleton } from 'app/pages/system/forms';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import FormEditor from 'app/shared/components/forms/FormEditor';
import dayjs from 'dayjs';
import withFormTemplateContent, { WithFormContentPropTypes } from 'hocs/withFormTemplateContent';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { IconDirection } from 'constants/coreDesignSystem';
import { LAST_UPDATED_DATE_FORMAT } from 'constants/date';
import { DynamicFieldType } from 'constants/dynamicFields';
import { IS_PRODUCTION } from 'constants/envs';
import { FeatureFlag } from 'constants/featureFlags';
import { SigningSignerType } from 'constants/formBuilder';
import { FormType, FormViewerNavigationType, NavigationStateType } from 'constants/forms';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useFormTemplateActions from 'hooks/useFormTemplateActions';
import { getIsFormNonOverridable, getPrismEditorBlockReason, isPdfFormType } from 'utils/forms';

import { getIsFromInternalCompany, getMasterPermissionsToPage } from 'store/auth/selectors';
import { getDynamicFieldsSchemaAction, resetFormTemplateAction } from 'store/forms/actions';
import {
  selectDynamicFieldsNamesById,
  selectFormNavigationStateType,
  selectFormNavigationType,
} from 'store/forms/selectors';
import { assignFormFromTaskDraftAction } from 'store/loanApplications/actions';
import { selectFormTaskDraftFields } from 'store/loanApplications/selectors';
import { getDocumentTypesAction } from 'store/system/actions';
import { selectDocumentTypeNameById } from 'store/system/selectors';

import FormChangeHistoryButton from '../FormChangeHistory/FormChangeHistoryButton';
import { FormsTestIds } from '../Forms.testIds';

import PdfEditor from './PdfEditor';

const getLastUpdatedText = ({ version, lastModified, isHistorySnapshotMode }) => {
  const prefix = isHistorySnapshotMode ? '' : 'Last updated on ';
  const suffix = isHistorySnapshotMode ? ', revision ' : ' to revision ';

  return `${prefix}${dayjs(lastModified).format(LAST_UPDATED_DATE_FORMAT)}${suffix}${version}`;
};

const FormViewer = ({
  form,
  setIsLoading,
  isLoading,
  formParams,
  onEmailTask,
  isShowAssignFormIconButton,
  onAssignFormSuccessCallback,
  customOnAssignFormClick,
  isNeedCloseButton,
  onClose,
}) => {
  const navigationType = useSelector(selectFormNavigationType);
  const navigationStateType = useSelector(selectFormNavigationStateType);
  const isNavigationNotRequired = navigationType === FormViewerNavigationType.STATE;
  const isNavigationStateTypeEdit = navigationStateType === NavigationStateType.EDIT;

  const pathParams = useParams();

  const {
    historyEntityId,
    selectedPage = 1,
    formId,
  } = isNavigationNotRequired ? formParams : pathParams;

  const dispatch = useDispatch();

  const [isInitiated, setIsInitiated] = useState(false);

  const formTaskDraftFields = useSelector(selectFormTaskDraftFields);
  const dynamicFieldsNamesById = useSelector(selectDynamicFieldsNamesById);

  const {
    state,
    template,
    version,
    lastModified,
    name,
    companyId,
    formType,
    documentTypeId,
    isSeededSystemForm,
    isGlobal,
  } = form;

  const elements = _.get(form, 'pdfMetadata.elements', []);

  const editorConfig = useMemo(
    () => ({
      dynamicFieldConfig: {
        dynamicFieldsNamesById,
      },
    }),
    [dynamicFieldsNamesById],
  );

  const isSignersNumberSpecifyingNeeded = useMemo(
    () =>
      (formType === FormType.HTML &&
        template?.includes(`data-hellosign-signer="${SigningSignerType.ALL_BORROWERS}"`)) ||
      (formType === FormType.CUSTOM && elements.some(({ helloSignTag }) => helloSignTag)),
    [formType, template, elements],
  );

  const prismBlockReason = useMemo(
    () => getPrismEditorBlockReason({ template, isSeededSystemForm }),
    [template, isSeededSystemForm],
  );

  const documentTypeName = useSelector(selectDocumentTypeNameById(documentTypeId));
  const { isEditAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.FORMS));
  const isUserFromInternalCompany = useSelector(getIsFromInternalCompany);
  const isDocumentRelatedToInternalCompany = checkIsInternalCompany({ companyId });

  const { onEditForm, onPreviewForm, onPreviewWithLoanForm, onViewForm, onSaveContent } =
    useFormTemplateActions(setIsLoading);

  const isPreviewFormBasedOnLoanIdAvailable = useFeatureToggle(
    FeatureFlag.PreviewFormBasedOnLoanId,
  );
  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isOverrideActive = isOverrideEnabled && !isUserFromInternalCompany;

  useEffectSafeStrictMode(() => {
    dispatch(getDocumentTypesAction());
  }, [dispatch]);

  useEffectSafeStrictMode(() => {
    // Best-effort metadata: the form renders even if the schema fails to load, so we already
    // swallow the rejection. Pass `silent` so a form referencing a deleted document type (the
    // schema endpoint 404s "DocumentType doesn't exist" — backend follow-up FH-1695) does not
    // surface a support-ticket error toast on open.
    dispatch(
      getDynamicFieldsSchemaAction({
        formId,
        dynamicFieldsScope: DynamicFieldType.FORMS,
        silent: true,
      }),
    )
      .catch(Error)
      .finally(() => {
        setIsInitiated(true);
      });
  }, [dispatch, formId]);

  const onAssignFormClick = useCallback(() => {
    setIsLoading(true);
    dispatch(
      assignFormFromTaskDraftAction({ formBundleId: formId, draftFields: formTaskDraftFields }),
    )
      .then(() => onAssignFormSuccessCallback())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, formId, formTaskDraftFields, setIsLoading, onAssignFormSuccessCallback]);

  const onEditClick = useCallback(() => {
    onEditForm({ state, formId, formType, selectedPage });
  }, [formId, onEditForm, state, formType, selectedPage]);

  const onPreviewClick = useCallback(
    () =>
      onPreviewForm({
        formId,
        formType,
        selectedPage,
        historyEntityId,
        isSignersNumberSpecifyingNeeded,
        coborrowersNumber: !isSignersNumberSpecifyingNeeded ? -1 : undefined,
        nonborrowersNumber: !isSignersNumberSpecifyingNeeded ? -1 : undefined,
      }),
    [
      formId,
      onPreviewForm,
      formType,
      selectedPage,
      historyEntityId,
      isSignersNumberSpecifyingNeeded,
    ],
  );

  const onPreviewFormBasedOnLoan = useCallback(
    () => onPreviewWithLoanForm({ formId, formType, selectedPage, historyEntityId }),
    [formId, onPreviewWithLoanForm, formType, selectedPage, historyEntityId],
  );

  const onCancelHistoryViewClick = useCallback(
    async e => {
      e.stopPropagation();

      await dispatch(resetFormTemplateAction());
      onViewForm({ formId, state, formType, selectedPage });
    },
    [dispatch, formId, onViewForm, state, formType, selectedPage],
  );

  const onRestoreRevision = useCallback(() => {
    onSaveContent({ formId, state, content: form.template, formType, selectedPage, elements });
  }, [form, formId, onSaveContent, state, formType, selectedPage, elements]);

  const isHistorySnapshotMode = !!historyEntityId;
  const isUpdated = version > 1;
  // An HTML form's first revision is an empty template; a PDF form's first revision already
  // carries the uploaded document and its fields, so it is previewable from the start.
  const isPreviewDisabled = !isPdfFormType(formType) && !isUpdated;

  const header = useMemo(() => {
    const docTypeStr = documentTypeName ? ` (${documentTypeName})` : '';
    const text = `${name}${docTypeStr} - ${getLastUpdatedText({
      version,
      lastModified,
      isHistorySnapshotMode,
    })}`;

    return (
      <>
        {isHistorySnapshotMode && (
          <ButtonWithTooltip
            disableRipple
            data-testid={FormsTestIds.FORM_VIEWER_BACK_BUTTON}
            disabled={isLoading}
            icon={ArrowCheckIcon}
            iconButtonClassName="mr-4"
            iconDirection={IconDirection.LEFT}
            title="Back to the current form"
            onClick={onCancelHistoryViewClick}
          />
        )}
        <span title={text}>{text}</span>
      </>
    );
  }, [
    name,
    documentTypeName,
    version,
    lastModified,
    isHistorySnapshotMode,
    isLoading,
    onCancelHistoryViewClick,
  ]);

  const formActions = useMemo(() => {
    const isRootLack =
      (!isUserFromInternalCompany && isDocumentRelatedToInternalCompany) ||
      formType === FormType.CUSTOM;

    const isEditDisabled = isOverrideActive
      ? getIsFormNonOverridable({ formType, isCustom: form.isCustom })
      : isRootLack;

    const previewTitle = isHistorySnapshotMode ? 'Preview this form revision' : 'Preview form';

    const buttons = [
      {
        disabled: isLoading || isPreviewDisabled,
        icon: ScannerIcon,
        testName: 'scanner',
        title: isPreviewDisabled ? 'No preview to show' : 'Preview form based on loan',
        onClick: onPreviewFormBasedOnLoan,
        disablePortal: true,
        isRendered: !IS_PRODUCTION && isPreviewFormBasedOnLoanIdAvailable,
      },
      {
        disabled: isLoading,
        icon: NoteIcon,
        testName: 'assignment',
        title: 'Assign form',
        disablePortal: true,
        onClick: customOnAssignFormClick || onAssignFormClick,
        isRendered: !!isShowAssignFormIconButton,
      },
      {
        disabled: isLoading,
        icon: EmailIcon,
        testName: 'email',
        title: 'Email to borrower',
        onClick: onEmailTask,
        disablePortal: true,
        isRendered: !!onEmailTask,
      },
      {
        disabled: isLoading || isEditDisabled,
        icon: EditPenIcon,
        testName: 'edit',
        title: isEditDisabled
          ? 'There is no possibility to edit global form template'
          : 'Edit form content',
        onClick: onEditClick,
        disablePortal: true,
        disableRipple: true,
        isRendered: !isHistorySnapshotMode,
      },
      {
        disabled: isLoading || isPreviewDisabled,
        icon: ShowIcon,
        testName: 'visibility',
        title: isPreviewDisabled ? 'No preview to show' : previewTitle,
        onClick: onPreviewClick,
        disablePortal: true,
        isRendered: true,
      },
      {
        disabled: isLoading || isRootLack,
        icon: ResetIcon,
        testName: 'restore_page',
        title: isRootLack
          ? 'Should not be able to restore revision of global form template'
          : 'Restore this revision',
        onClick: onRestoreRevision,
        disableRipple: true,
        disablePortal: true,
        isRendered: isEditAllowed && isHistorySnapshotMode,
      },
      {
        disabled: isLoading,
        icon: CancelIcon,
        testName: 'close',
        title: 'Close the form',
        disablePortal: true,
        onClick: onClose,
        isRendered: isNeedCloseButton,
      },
    ];

    return (
      <>
        <FormChangeHistoryButton formId={formId} formName={name} isGlobalForm={isGlobal} />
        {buttons.map(({ isRendered, testName, ...props }, index) =>
          isRendered ? (
            <ButtonWithTooltip
              key={`${props.title}-${index}`}
              {...props}
              data-testid={FormsTestIds.getFormViewerActionButtonId(testName)}
            />
          ) : null,
        )}
      </>
    );
  }, [
    isUserFromInternalCompany,
    isDocumentRelatedToInternalCompany,
    isOverrideActive,
    formType,
    form.isCustom,
    isLoading,
    isPreviewDisabled,
    onPreviewFormBasedOnLoan,
    isPreviewFormBasedOnLoanIdAvailable,
    customOnAssignFormClick,
    onAssignFormClick,
    isShowAssignFormIconButton,
    onEmailTask,
    onEditClick,
    isEditAllowed,
    isHistorySnapshotMode,
    onPreviewClick,
    onRestoreRevision,
    onClose,
    isNeedCloseButton,
    formId,
    name,
    isGlobal,
  ]);

  if (isPdfFormType(formType)) {
    return (
      <PdfEditor
        form={form}
        formActions={formActions}
        header={header}
        initialSelectedPage={selectedPage}
      />
    );
  }

  if (!isInitiated) {
    return <FormBuilderSkeleton />;
  }

  if (isNavigationNotRequired && isNavigationStateTypeEdit) {
    return <FormBuilder form={form} formParams={formParams} />;
  }

  return (
    <FormEditor
      isLegacyTemplateHtml
      readOnly
      config={editorConfig}
      form={form}
      formActions={formActions}
      header={header}
      initialDocumentJson={form.templateJson}
      isTemplateSavedInPrism={form.isTemplateSavedInPrism}
      prismBlockReason={prismBlockReason}
      value={template}
    />
  );
};

FormViewer.propTypes = {
  ...WithFormContentPropTypes,
  ...WithLoadingPropTypes,
  customOnAssignFormClick: PropTypes.func,
};

export default compose(withFormTemplateContent, withLoading, memo)(FormViewer);
